/**
 * Version utility for Atlas Viewer
 *
 * Provides a clean API for accessing the application version
 * with fallback support for different environments
 */

/**
 * Get the application version
 *
 * This function provides multiple ways to access the version:
 * 1. __APP_VERSION__ - Injected by Vite from package.json (preferred)
 * 2. import.meta.env.VITE_APP_VERSION - From environment variables (backward compatibility)
 * 3. 'unknown' - Fallback if no version is available
 *
 * @returns {string} The application version
 */
export function getAppVersion(): string {
  // Primary: Use the version constant injected by Vite
  if (typeof __APP_VERSION__ !== 'undefined') {
    return __APP_VERSION__;
  }

  // Secondary: Check environment variables (for backward compatibility)
  if (import.meta.env.VITE_APP_VERSION) {
    return import.meta.env.VITE_APP_VERSION;
  }

  // Fallback: Unknown version
  return 'unknown';
}

/**
 * Check if we're running in development mode
 *
 * @returns {boolean} True if in development mode
 */
export function isDevelopment(): boolean {
  return import.meta.env.MODE === 'development';
}

/**
 * Get a formatted version string for display
 *
 * @returns {string} Formatted version string (e.g., "v1.2.3" or "v1.2.3-dev")
 */
export function getFormattedVersion(): string {
  const version = getAppVersion();
  const prefix = version.startsWith('v') ? '' : 'v';
  const suffix = isDevelopment() ? '-dev' : '';

  return `${prefix}${version}${suffix}`;
}

/**
 * Get version information object
 *
 * @returns {object} Version information including raw version, formatted version, and environment
 */
export function getVersionInfo() {
  return {
    raw: getAppVersion(),
    formatted: getFormattedVersion(),
    isDev: isDevelopment(),
    mode: import.meta.env.MODE,
    timestamp: new Date().toISOString(),
  };
}
